export interface BarChartOptions {
  orientation: 'vertical' | 'horizontal';
  mode?: 'stacked' | 'grouped';
  colors?: any;
  colorScheme?: string[];
  showGridLines?: boolean;
  xAxisLabel?: string;
  showBarLabel?: boolean;
  isCost?: boolean;
  showLegendOnTop?: boolean;
}

export const barChartBuilder = (
  categories: string[],
  series: any[],
  {
    orientation,
    mode = 'grouped',
    colors,
    colorScheme,
    showGridLines = false,
    xAxisLabel,
    showBarLabel = true,
    isCost = false,
    showLegendOnTop = false,
  }: BarChartOptions,
) => {
 
  const defaultColors = [
    colors?.widget?.colors?.color1,
    colors?.widget?.colors?.color2,
    colors?.widget?.colors?.color3,
    colors?.widget?.colors?.color4,
    colors?.widget?.colors?.color5,
  ];

  const formatCost = (v: number) =>
    Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : '';

  const formatNumber = (v: number) =>
    Number.isFinite(v) ? `${v}` : '';

  const valueFormatter = (v: number) =>
    isCost ? formatCost(v) : formatNumber(v);

  const palette = colorScheme?.length ? colorScheme : defaultColors;

  const processedSeries = series.map((s, index) => ({
    name: s.name,
    type: 'bar',
    data: s.data,
    stack: mode === 'stacked' ? 'stacked-bars' : undefined,
    label: {
      show: showBarLabel && mode !== 'stacked',
      position: orientation === 'vertical' ? 'top' : 'right',
      color: colors?.widget?.chart?.labelColorSecondary || '#666',
      fontSize: 10,
      formatter: (params: any) => valueFormatter(params.value),
    },
    itemStyle: {
      color: s.color || palette[index % palette.length],
    },
  }));

  return {
    backgroundColor: colors?.widget?.background?.chart,
    textStyle: { color: colors?.widget?.chart?.labelColorSecondary },

    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        const tooltipTitle = params?.[0]?.axisValueLabel ?? '';
        const rows = params
          .map(
            (p) =>
              `${p.marker} ${p.seriesName}: <strong>${valueFormatter(p.value)}</strong>`,
          )
          .join('<br/>');
        return `${tooltipTitle}<br/>${rows}`;
      },
    },

    legend: {
      type: 'scroll',
      left: 0,
      icon: 'rect',
      itemWidth: 14,
      itemHeight: 14,
      ...(showLegendOnTop ? { top: 0 } : { bottom: 0 }),
      textStyle: { color: colors?.widget?.chart?.legendTextColor },
      data: processedSeries.map((s) => s.name),
    },

    grid: {
      left: '5%',
      right: '5%',
      bottom: showLegendOnTop ? '12%' : '18%',
      top: showLegendOnTop ? '18%' : '5%',
      containLabel: true,
    },

    xAxis:
      orientation === 'vertical'
        ? [
            {
              name: xAxisLabel || undefined,
              nameGap: xAxisLabel ? 35 : 20,
              nameLocation: 'middle',
              type: 'category',
              data: categories,
              axisLabel: {
                color: colors?.widget?.chart?.labelColorSecondary,
              },
              axisTick: { show: false },
            },
          ]
        : [
            {
              type: 'value',
              axisLabel: {
                color: colors?.widget?.chart?.labelColorSecondary,
                formatter: (val: number) => valueFormatter(val),
              },
              splitLine: { show: showGridLines },
            },
          ],

    yAxis:
      orientation === 'vertical'
        ? [
            {
              type: 'value',
              axisLabel: {
                color: colors?.widget?.chart?.labelColorSecondary,
                formatter: (val: number) => valueFormatter(val),
              },
              splitLine: { show: showGridLines },
            },
          ]
        : [
            {
              type: 'category',
              data: categories,
              axisLabel: {
                color: colors?.widget?.chart?.labelColorSecondary,
              },
            },
          ],

    series: processedSeries,
  };
};